import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, loadTranslations } from '../lib/i18n';
import { settingsApi } from '../lib/api';
import { getLanguage, LANGUAGES as ALL_LANGUAGES, LANGUAGE_CATEGORIES as LANG_CATS } from '../lib/languages';

// Re-export for convenience
export { ALL_LANGUAGES as LANGUAGES };

// Transform LANGUAGE_CATEGORIES to array format for UI
export const LANGUAGE_CATEGORIES = [
  {
    name: 'Major Languages',
    languages: LANG_CATS.major?.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean) || []
  },
  {
    name: 'European Languages',
    languages: LANG_CATS.europe?.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean) || []
  },
  {
    name: 'Turkic & Caucasus',
    languages: LANG_CATS.turkicCaucasus?.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean) || []
  },
  {
    name: 'Middle East',
    languages: LANG_CATS.middleEast?.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean) || []
  },
  {
    name: 'South Asia',
    languages: LANG_CATS.southAsia?.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean) || []
  },
  {
    name: 'Southeast Asia',
    languages: LANG_CATS.southeastAsia?.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean) || []
  },
  {
    name: 'African Languages',
    languages: LANG_CATS.africa?.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean) || []
  },
  {
    name: 'Other Languages',
    languages: LANG_CATS.other?.map(code => ALL_LANGUAGES.find(l => l.code === code)).filter(Boolean) || []
  },
].filter(category => category.languages.length > 0); // Remove empty categories

// ============================================================
// AUTO LANGUAGE DETECTION - Browser + Geolocation
// ============================================================

// IP-based country detection (async)
async function detectCountryFromIP(): Promise<string | null> {
  try {
    console.log('🌍 [LANGUAGE DETECT] Detecting country from IP...');
    
    // Use ipapi.co free API (no API key required, 1000 requests/day)
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) {
      console.warn('⚠️ [LANGUAGE DETECT] IP API request failed');
      return null;
    }
    
    const data = await response.json();
    const countryCode = data.country_code?.toUpperCase();
    
    console.log(`✅ [LANGUAGE DETECT] Country detected from IP: ${countryCode}`);
    return countryCode;
  } catch (error) {
    console.warn('⚠️ [LANGUAGE DETECT] Failed to detect country from IP:', error);
    return null;
  }
}

// Map country code to language code
function getLanguageFromCountry(countryCode: string): string {
  const countryToLanguage: Record<string, string> = {
    'TR': 'tr', // Turkey -> Turkish
    'US': 'en', 'GB': 'en', 'CA': 'en', 'AU': 'en', 'NZ': 'en', 'IE': 'en', // English
    'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'CL': 'es', 'PE': 'es', // Spanish
    'DE': 'de', 'AT': 'de', 'CH': 'de', // German
    'RU': 'ru', 'BY': 'ru', 'KZ': 'ru', 'UA': 'ru', // Russian
    'AZ': 'az', // Azerbaijan
    'IT': 'it', // Italian
    'FR': 'fr', 'BE': 'fr', 'LU': 'fr', // French
    'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'JO': 'ar', 'LB': 'ar', // Arabic
    'PT': 'pt', 'BR': 'pt', // Portuguese
    'NL': 'nl', // Dutch
    'PL': 'pl', // Polish
    'JP': 'ja', // Japanese
    'KR': 'ko', // Korean
    'CN': 'zh', // Chinese
    'IN': 'hi', // India
    'BD': 'bn', // Bangladesh
    'PK': 'ur', // Pakistan
    'IR': 'fa', // Iran
    'IL': 'he', // Israel
    'SE': 'sv', // Swedish
    'NO': 'no', // Norwegian
    'DK': 'da', // Danish
    'FI': 'fi', // Finnish
    'GR': 'el', // Greek
    'HU': 'hu', // Hungarian
    'CZ': 'cs', // Czech
    'RO': 'ro', // Romanian
    'BG': 'bg', // Bulgarian
    'RS': 'sr', // Serbian
    'HR': 'hr', // Croatian
    'SK': 'sk', // Slovak
    'SI': 'sl', // Slovenian
    'EE': 'et', // Estonian
    'LV': 'lv', // Latvian
    'LT': 'lt', // Lithuanian
    'AL': 'sq', // Albanian
    'MK': 'mk', // Macedonian
    'GE': 'ka', // Georgian
    'AM': 'hy', // Armenian
    'TH': 'th', // Thai
    'VN': 'vi', // Vietnamese
  };
  
  return countryToLanguage[countryCode] || 'en'; // Default to English
}

function detectBrowserLanguage(): string {
  // Get timezone first - this is the primary detection method
  const timezone = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || '';
  console.log(`🌍 [LANGUAGE DETECT] Browser timezone: ${timezone}`);
  
  // Map timezone to language
  const timezoneToLanguage: Record<string, string> = {
    // Turkey
    'Europe/Istanbul': 'tr', 'Asia/Istanbul': 'tr', 'Europe/Ankara': 'tr', 'Europe/İstanbul': 'tr',
    // USA
    'America/New_York': 'en', 'America/Chicago': 'en', 'America/Denver': 'en', 'America/Los_Angeles': 'en', 'America/Phoenix': 'en', 'America/Anchorage': 'en', 'America/Honolulu': 'en',
    // UK
    'Europe/London': 'en',
    // Germany
    'Europe/Berlin': 'de', 'Europe/Zurich': 'de', 'Europe/Vienna': 'de',
    // France
    'Europe/Paris': 'fr',
    // Spain
    'Europe/Madrid': 'es',
    // Italy
    'Europe/Rome': 'it',
    // Russia
    'Europe/Moscow': 'ru', 'Europe/Volgograd': 'ru',
    // Arabic countries
    'Asia/Riyadh': 'ar', 'Asia/Dubai': 'ar', 'Asia/Qatar': 'ar', 'Asia/Kuwait': 'ar',
    // China
    'Asia/Shanghai': 'zh', 'Asia/Hong_Kong': 'zh',
    // Japan
    'Asia/Tokyo': 'ja',
    // Korea
    'Asia/Seoul': 'ko',
    // Brazil
    'America/Sao_Paulo': 'pt', 'America/Brasilia': 'pt',
    // Netherlands
    'Europe/Amsterdam': 'nl',
    // Poland
    'Europe/Warsaw': 'pl',
    // Romania
    'Europe/Bucharest': 'ro',
    // Greece
    'Europe/Athens': 'el',
    // Bulgaria
    'Europe/Sofia': 'bg',
    // Ukraine
    'Europe/Kiev': 'uk',
    // Hungary
    'Europe/Budapest': 'hu',
    // Czech
    'Europe/Prague': 'cs',
    // Sweden
    'Europe/Stockholm': 'sv',
    // Norway
    'Europe/Oslo': 'no',
    // Denmark
    'Europe/Copenhagen': 'da',
    // Finland
    'Europe/Helsinki': 'fi',
    // Portugal
    'Europe/Lisbon': 'pt',
  };
  
  // Check exact timezone match
  for (const [tz, lang] of Object.entries(timezoneToLanguage)) {
    if (timezone.includes(tz) || tz.includes(timezone)) {
      console.log(`✅ [LANGUAGE DETECT] Detected ${lang} from timezone: ${timezone}`);
      return lang;
    }
  }
  
  // Check partial matches
  if (timezone.startsWith('Europe/')) return 'en'; // Default to English for Europe
  if (timezone.startsWith('America/')) return 'en';
  
  // Default to Turkish
  console.log(`⚠️ [LANGUAGE DETECT] No timezone match, defaulting to Turkish`);
  return 'tr';
}

async function getInitialLanguageAsync(): Promise<string> {
  // 1. Check localStorage first (user's previous selection)
  const savedLang = localStorage.getItem('appLanguage');
  
  // Even if saved, check timezone - if user is in Turkey, prefer Turkish
  if (savedLang) {
    try {
      const timezone = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || '';
      if (timezone.includes('Istanbul') || timezone.includes('Ankara') || timezone.includes('Turkey') || timezone.includes('Europe/Istanbul') || timezone.includes('Asia/Istanbul')) {
        console.log(`🌍 [LANGUAGE DETECT] User in Turkey (timezone), overriding saved language to Turkish`);
        localStorage.setItem('appLanguage', 'tr');
        return 'tr';
      }
    } catch {}
    console.log(`💾 Using saved language from localStorage: ${savedLang}`);
    return savedLang;
  }
  
  // Check timezone first before IP detection
  try {
    const timezone = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || '';
    if (timezone.includes('Istanbul') || timezone.includes('Ankara') || timezone.includes('Turkey') || timezone.includes('Europe/Istanbul') || timezone.includes('Asia/Istanbul')) {
      console.log(`🌍 [LANGUAGE DETECT] Detected Turkey from timezone, using Turkish`);
      localStorage.setItem('appLanguage', 'tr');
      return 'tr';
    }
  } catch {}
  
  // 2. Try IP-based country detection
  try {
    const countryCode = await detectCountryFromIP();
    if (countryCode) {
      const langFromCountry = getLanguageFromCountry(countryCode);
      console.log(`🌍 Language detected from IP (${countryCode}): ${langFromCountry}`);
      localStorage.setItem('appLanguage', langFromCountry);
      return langFromCountry;
    }
  } catch (error) {
    console.warn('⚠️ IP detection failed, falling back to browser detection');
  }
  
  // 3. Fallback to browser language detection
  const detectedLang = detectBrowserLanguage();
  console.log(`🔍 Auto-detected language from browser: ${detectedLang}`);
  
  // Save to localStorage
  localStorage.setItem('appLanguage', detectedLang);
  
  return detectedLang;
}

function getInitialLanguage(): string {
  // 1. Priority: User's manual selection from localStorage
  const savedLang = localStorage.getItem('appLanguage');
  if (savedLang) {
    console.log(`🏠 [LanguageContext] Using saved language from localStorage: ${savedLang}`);
    return savedLang;
  }
  
  // 2. Priority: Timezone-based detection for Turkey
  try {
    const timezone = Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone || '';
    if (timezone.includes('Istanbul') || timezone.includes('Ankara') || timezone.includes('Turkey') || 
        timezone.includes('Europe/Istanbul') || timezone.includes('Asia/Istanbul')) {
      console.log(`🌍 [LanguageContext] No saved language, but user is in Turkey (timezone). Initializing to Turkish.`);
      // Note: Don't set localStorage here yet, let the provider handle persistence
      return 'tr';
    }
  } catch (e) {
    console.warn('[LanguageContext] Timezone detection failed:', e);
  }
  
  // 3. Priority: Browser settings or IP-based (will be handled by useEffect)
  const browserLang = detectBrowserLanguage();
  console.log(`🌐 [LanguageContext] Using browser language as initial value: ${browserLang}`);
  return browserLang;
}

export interface CurrencySettings {
  currency: string;
  currencyPosition: 'left' | 'right';
  numberFormat: 'dot-comma' | 'comma-dot' | 'space-comma';
  showDecimals: boolean;
}

// Helper function to convert language code to locale format for date formatting
export function getDateFormatLocale(language: string): string {
  const localeMap: Record<string, string> = {
    'tr': 'tr-TR',
    'en': 'en-US',
    'es': 'es-ES',
    'de': 'de-DE',
    'ru': 'ru-RU',
    'az': 'az-AZ',
    'it': 'it-IT',
    'ar': 'ar-SA',
    'fr': 'fr-FR',
    'pt': 'pt-BR',
    'nl': 'nl-NL',
    'pl': 'pl-PL',
    'uk': 'uk-UA',
    'ja': 'ja-JP',
    'ko': 'ko-KR',
    'zh': 'zh-CN',
    'hi': 'hi-IN',
    'bn': 'bn-BD',
    'ur': 'ur-PK',
    'fa': 'fa-IR',
    'he': 'he-IL',
    'sv': 'sv-SE',
    'no': 'nb-NO',
    'da': 'da-DK',
    'fi': 'fi-FI',
    'el': 'el-GR',
    'hu': 'hu-HU',
    'cs': 'cs-CZ',
    'ro': 'ro-RO',
    'bg': 'bg-BG',
    'sr': 'sr-RS',
    'hr': 'hr-HR',
    'sk': 'sk-SK',
    'sl': 'sl-SI',
    'et': 'et-EE',
    'lv': 'lv-LV',
    'lt': 'lt-LT',
    'sq': 'sq-AL',
    'mk': 'mk-MK',
    'ka': 'ka-GE',
    'hy': 'hy-AM',
    'th': 'th-TH',
    'vi': 'vi-VN',
    'id': 'id-ID',
    'ms': 'ms-MY',
    'tl': 'tl-PH',
  };
  
  // Return mapped locale or fallback to language-LANGUAGE format
  return localeMap[language] || `${language}-${language.toUpperCase()}`;
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isLoading: boolean;
  currencySettings: CurrencySettings;
  refreshSettings: () => Promise<void>;
}

// Create a default context value for safe fallback
const defaultContextValue: LanguageContextType = {
  language: 'tr',
  setLanguage: () => {},
  t: (key: string) => key,
  isLoading: false,
  currencySettings: { currency: 'TRY', currencyPosition: 'right', numberFormat: 'dot-comma', showDecimals: true },
  refreshSettings: async () => {},
};

export const LanguageContext = createContext<LanguageContextType>(defaultContextValue);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage());
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  
  const [currencySettings, setCurrencySettings] = useState<CurrencySettings>(() => {
    const saved = localStorage.getItem('currencySettings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved currency settings:', e);
      }
    }
    return { currency: 'TRY', currencyPosition: 'right', numberFormat: 'dot-comma', showDecimals: true };
  });

  // 🌍 IP-based language detection on first load
  useEffect(() => {
    const detectLanguageOnFirstLoad = async () => {
      const savedLang = localStorage.getItem('appLanguage');
      if (!savedLang) {
        // No saved language - detect from IP
        const detectedLang = await getInitialLanguageAsync();
        setLanguageState(detectedLang);
        console.log(`🌍 [LanguageProvider] Language set from IP/browser: ${detectedLang}`);
      }
    };
    
    detectLanguageOnFirstLoad();
  }, []);

  useEffect(() => {
    loadLanguage();
  }, []);

  useEffect(() => {
    loadLanguageTranslations();
  }, [language]);

  const loadLanguage = async () => {
    try {
      // Check if tenant ID exists before making API call
      const tenantId = localStorage.getItem('tenantId');
      // Giriş kullanıcıyı 'user' (JSON) olarak yazar; 'user_id' yalnızca eski oturumlarda var
      const isLoggedIn = localStorage.getItem('user') || localStorage.getItem('user_id');
      const currentLocalLang = localStorage.getItem('appLanguage');
      
      // Skip backend language loading if:
      // 1. No tenant ID (signup/login page)
      // 2. Not logged in yet
      if (!tenantId || !isLoggedIn) {
        console.log('[LanguageContext] No tenant/user yet, keeping initial language');
        setLoading(false);
        return;
      }
      
      console.log('[LanguageContext] Syncing language from backend settings...');
      const data = await settingsApi.get();
      
      // Only override local language if backend has a valid language AND it's different
      // If backend says 'tr' but local is something else, we should be careful 
      // as 'tr' is often the default value
      const backendLang = data.settings?.language;
      
      if (backendLang && backendLang !== language) {
        console.log(`📥 [LanguageContext] Updating language from backend: ${backendLang} (was ${language})`);
        setLanguageState(backendLang as Language);
        localStorage.setItem('appLanguage', backendLang);
      } else {
        console.log(`✅ [LanguageContext] Language already in sync or no backend override needed (Backend: ${backendLang}, Local: ${language})`);
      }
      
      const newSettings: CurrencySettings = { 
        currency: data.settings?.currency || 'TRY', 
        currencyPosition: data.settings?.currency_position || data.settings?.currencyPosition || 'right', 
        numberFormat: data.settings?.number_format || data.settings?.numberFormat || 'dot-comma', 
        showDecimals: data.settings?.show_decimals !== undefined 
          ? data.settings.show_decimals 
          : (data.settings?.showDecimals !== undefined ? data.settings.showDecimals : true)
      };
      
      setCurrencySettings(newSettings);
      localStorage.setItem('currencySettings', JSON.stringify(newSettings));
    } catch (error) {
      console.error('[LanguageContext] Sync error:', error);
      // Fallback: keep current state
    } finally {
      setLoading(false);
    }
  };

  const loadLanguageTranslations = async () => {
    console.log(`[LanguageContext] Loading translations for language: ${language}`);
    try {
      const trans = await loadTranslations(language);
      console.log(`[LanguageContext] Translations loaded for ${language}:`, Object.keys(trans).length, 'keys');
      console.log(`[LanguageContext] Sample: signup.title =`, trans['signup.title']);
      setTranslations(trans);
    } catch (error) {
      console.error('Translation load error:', error);
    }
  };

  const setLanguage = async (lang: Language) => {
    console.log(`[LanguageContext] setLanguage called with: ${lang}`);
    console.log(`[LanguageContext] Previous language: ${language}`);
    
    setLanguageState(lang);
    
    // ✅ ALWAYS save to localStorage (for login page persistence)
    localStorage.setItem('appLanguage', lang);
    console.log(`💾 Language saved to localStorage: ${lang}`);
    
    // Only save to backend if user is logged in (has tenant ID)
    const tenantId = localStorage.getItem('tenantId');
    if (!tenantId) {
      console.log('💡 Language changed but not logged in yet - skipping settings save');
      return;
    }
    
    try {
      await settingsApi.update({ language: lang });
      console.log(`✅ Language saved to backend: ${lang}`);
    } catch (error) {
      console.error('Language save error:', error);
    }
  };

  const t = (key: string): string => {
    return translations[key] || key;
  };

  // Don't block rendering during load - provide default context values
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, isLoading: loading, currencySettings, refreshSettings: loadLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  // Always return context - now has default value so never undefined
  return context;
}

// Alias for compatibility
export const useTranslation = () => {
  const { t } = useLanguage();
  return { t };
};

// Backward compatibility alias for useI18n
export const useI18n = useLanguage;

// Helper function to translate database values based on language
export function translateDbValue(value: string, language: string): string {
  const dbTranslations: Record<string, Record<string, string>> = {
    // Order Status
    pending: { tr: 'İşlemde', en: 'In Process', fr: 'En cours', es: 'En Proceso', ru: 'В обработке', de: 'In Bearbeitung', az: 'İşlənir', it: 'In Lavorazione', ar: 'قيد المعالجة' },
    'in_progress': { tr: 'İşlemde', en: 'In Process', fr: 'En cours', es: 'En Proceso', ru: 'В обработке', de: 'In Bearbeitung', az: 'İşlənir', it: 'In Lavorazione', ar: 'قيد المعالجة' },
    ready: { tr: 'Hazır', en: 'Ready', fr: 'Prêt', es: 'Listo', ru: 'Готово', de: 'Fertig', az: 'Hazırdır', it: 'Pronto', ar: 'جاهز' },
    delivered: { tr: 'Teslim Edildi', en: 'Delivered', fr: 'Livré', es: 'Entregado', ru: 'Доставлено', de: 'Geliefert', az: 'Çatdırılıb', it: 'Consegnato', ar: 'تم التسليم' },
    'pickup_pending': { tr: 'Alınacak', en: 'To Pickup', fr: 'À collecter', es: 'Por Recoger', ru: 'На забор', de: 'Abzuholen', az: 'Götürüləcək', it: 'Da Ritirare', ar: 'للاستلام' },
    'pickup_in_progress': { tr: 'Alınıyor', en: 'Picking Up', fr: 'En collecte', es: 'Recogiendo', ru: 'Забирается', de: 'Wird abgeholt', az: 'Götürülür', it: 'Ritirando', ar: 'جاري الاستلام' },
    'received': { tr: 'Alındı', en: 'Received', fr: 'Reçu', es: 'Recibido', ru: 'Получено', de: 'Erhalten', az: 'Alınıb', it: 'Ricevuto', ar: 'تم الاستلام' },
    
    // Categories
    erkek: { tr: 'Erkek', en: 'Men', es: 'Hombre', fr: 'Homme', ru: 'Мужчины', de: 'Herren', az: 'Kişi', it: 'Uomo', ar: 'رجال', pt: 'Homem', pl: 'Mężczyźni' },
    kadın: { tr: 'Kadın', en: 'Women', es: 'Mujer', fr: 'Femme', ru: 'Женщины', de: 'Damen', az: 'Qadın', it: 'Donna', ar: 'نساء', pt: 'Mulher', pl: 'Kobiety' },
    çocuk: { tr: 'Çocuk', en: 'Children', es: 'Niños', fr: 'Enfant', ru: 'Дети', de: 'Kinder', az: 'Uşaq', it: 'Bambini', ar: 'أطفال', pt: 'Criança', pl: 'Dzieci' },
    ev: { tr: 'Ev Tekstili', en: 'Home Textile', es: 'Textiles del Hogar', fr: 'Linge de Maison', ru: 'Домашний текстиль', de: 'Heimtextilien', az: 'Ev Tekstili', it: 'Tessuti per la Casa', ar: 'منسوجات منزلية', pt: 'Têxteis Lar', pl: 'Tekstylia domowe' },
    halı: { tr: 'Halı', en: 'Carpet', es: 'Alfombra', fr: 'Tapis', ru: 'Ковер', de: 'Teppich', az: 'Xalça', it: 'Tappeto', ar: 'سجاد', pt: 'Tapete', pl: 'Dywany' },
    ayakkabı: { tr: 'Ayakkabı', en: 'Shoes', es: 'Zapatos', fr: 'Chaussures', ru: 'Обувь', de: 'Schuhe', az: 'Ayaqqabı', it: 'Scarpe', ar: 'أحذية', pt: 'Calçado', pl: 'Obuwie' },
    perde: { tr: 'Perde', en: 'Curtain', es: 'Cortina', fr: 'Rideaux', ru: 'Штора', de: 'Vorhang', az: 'Pərdə', it: 'Tenda', ar: 'ستارة', pt: 'Cortinados', pl: 'Zasłony' },
    deri: { tr: 'Deri', en: 'Leather', es: 'Cuero', fr: 'Cuir', ru: 'Кожа', de: 'Leder', az: 'Dəri', it: 'Pelle', ar: 'جلد', pt: 'Couro', pl: 'Skóra' },
    özel: { tr: 'Özel', en: 'Special', es: 'Especial', fr: 'Spécial', ru: 'Особый', de: 'Besonders', az: 'Xüsusi', it: 'Speciale', ar: 'خاص', pt: 'Especial', pl: 'Specjalne' },
    spor: { tr: 'Spor', en: 'Sports', es: 'Deportes', fr: 'Sport', ru: 'Спорт', de: 'Sport', az: 'İdman', it: 'Sport', ar: 'رياضة', pt: 'Desporto', pl: 'Sport' },
    aksesuar: { tr: 'Aksesuar', en: 'Accessories', es: 'Accesorios', fr: 'Accessoires', ru: 'Аксессуары', de: 'Accessoires', az: 'Aksesuar', it: 'Accessori', ar: 'إكسسوارات', pt: 'Acessórios', pl: 'Akcesoria' },
    kumaş: { tr: 'Kumaş', en: 'Fabric', es: 'Tela', fr: 'Tissu', ru: 'Ткань', de: 'Stoff', az: 'Parça', it: 'Tessuto', ar: 'قماش', pt: 'Tecido', pl: 'Tkanina' },
    koltuk: { tr: 'Koltuk Yıkama', en: 'Sofa Cleaning', es: 'Limpieza de Sofá', fr: 'Nettoyage Canapé', ru: 'Чистка дивана', de: 'Sofareinigung', az: 'Divan Yuma', it: 'Pulizia Divano', ar: 'تنظيف الكنبة', pt: 'Limpeza de Sofá', pl: 'Czyszczenie sofy' },
    yerinde: { tr: 'Yerinde Temizlik', en: 'On-Site Cleaning', es: 'Limpieza in Situ', fr: 'Nettoyage sur Place', ru: 'Чистка на месте', de: 'Reinigung vor Ort', az: 'Yerində Təmizlənmə', it: 'Pulizia in Loco', ar: 'تنظيف في الموقع', pt: 'Limpeza no Local', pl: 'Czyszczenie na miejscu' },
    araç: { tr: 'Araç', en: 'Vehicle', es: 'Vehículo', fr: 'Véhicule', ru: 'Авто', de: 'Fahrzeug', az: 'Avtomobil', it: 'Veicolo', ar: 'مركبة', pt: 'Veículo', nl: 'Voertuig', pl: 'Pojazd' },
    diğer: { tr: 'Diğer', en: 'Other', es: 'Otro', fr: 'Autre', ru: 'Другое', de: 'Andere', az: 'Digər', it: 'Altro', ar: 'أخر', pt: 'Outro', pl: 'Inne' },
    // Berber / kuaför kategorileri (sipariş ekranı filtre etiketleri ve ürün kategori kimlikleri)
    bayan: { tr: 'Bayan', en: 'Women', es: 'Mujer', fr: 'Femme', ru: 'Женщины', de: 'Damen', az: 'Qadın', it: 'Donna', ar: 'نساء', pt: 'Mulher', pl: 'Kobiety', nl: 'Dames' },
    tırnak: { tr: 'Tırnak', en: 'Nails', es: 'Uñas', fr: 'Ongles', ru: 'Ногти', de: 'Nägel', az: 'Dırnaq', it: 'Unghie', ar: 'أظافر', pt: 'Unhas', pl: 'Paznokcie', nl: 'Nagels' },
    tirnak: { tr: 'Tırnak', en: 'Nails', es: 'Uñas', fr: 'Ongles', ru: 'Ногти', de: 'Nägel', az: 'Dırnaq', it: 'Unghie', ar: 'أظافر', pt: 'Unhas', pl: 'Paznokcie', nl: 'Nagels' },
    epilasyon: { tr: 'Epilasyon', en: 'Hair Removal', es: 'Depilación', fr: 'Épilation', ru: 'Эпиляция', de: 'Haarentfernung', az: 'Epilyasiya', it: 'Epilazione', ar: 'إزالة الشعر', pt: 'Depilação', pl: 'Depilacja', nl: 'Ontharing' },
    bakım: { tr: 'Bakım', en: 'Care', es: 'Cuidado', fr: 'Soins', ru: 'Уход', de: 'Pflege', az: 'Baxım', it: 'Cura', ar: 'العناية', pt: 'Cuidados', pl: 'Pielęgnacja', nl: 'Verzorging' },
    bakim: { tr: 'Bakım', en: 'Care', es: 'Cuidado', fr: 'Soins', ru: 'Уход', de: 'Pflege', az: 'Baxım', it: 'Cura', ar: 'العناية', pt: 'Cuidados', pl: 'Pielęgnacja', nl: 'Verzorging' },
    makyaj: { tr: 'Makyaj', en: 'Makeup', es: 'Maquillaje', fr: 'Maquillage', ru: 'Макияж', de: 'Make-up', az: 'Makiyaj', it: 'Trucco', ar: 'مكياج', pt: 'Maquilhagem', pl: 'Makijaż', nl: 'Make-up' },
    kuaför: { tr: 'Kuaför', en: 'Hairdresser', es: 'Peluquería', fr: 'Coiffure', ru: 'Парикмахерская', de: 'Friseur', az: 'Bərbər', it: 'Parrucchiere', ar: 'تصفيف الشعر', pt: 'Cabeleireiro', pl: 'Fryzjer', nl: 'Kapper' },
    kuafor: { tr: 'Kuaför', en: 'Hairdresser', es: 'Peluquería', fr: 'Coiffure', ru: 'Парикмахерская', de: 'Friseur', az: 'Bərbər', it: 'Parrucchiere', ar: 'تصفيف الشعر', pt: 'Cabeleireiro', pl: 'Fryzjer', nl: 'Kapper' },
    tümü: { tr: 'Tümü', en: 'All', es: 'Todo', fr: 'Tout', ru: 'Все', de: 'Alle', az: 'Hamısı', it: 'Tutti', ar: 'الكل', pt: 'Todos', pl: 'Wszystkie', nl: 'Alle' },
    tumu: { tr: 'Tümü', en: 'All', es: 'Todo', fr: 'Tout', ru: 'Все', de: 'Alle', az: 'Hamısı', it: 'Tutti', ar: 'الكل', pt: 'Todos', pl: 'Wszystkie', nl: 'Alle' },
    diger: { tr: 'Diğer', en: 'Other', es: 'Otro', fr: 'Autre', ru: 'Другое', de: 'Andere', az: 'Digər', it: 'Altro', ar: 'أخرى', pt: 'Outro', pl: 'Inne', nl: 'Overig' },

    // Service types
    'kuru temizleme': { tr: 'Kuru Temizleme', en: 'Dry Cleaning', es: 'Limpieza en Seco', fr: 'Nettoyage à Sec', ru: 'Химчистка', de: 'Chemische Reinigung', az: 'Quru Təmizləmə', it: 'Lavaggio a Secco', ar: 'تنظيف جاف' },
    yıkama: { tr: 'Yıkama', en: 'Washing', es: 'Lavado', fr: 'Lavage', ru: 'Стирка', de: 'Waschen', az: 'Yuma', it: 'Lavaggio', ar: 'غسيل' },
    ütü: { tr: 'Ütü', en: 'Ironing', es: 'Plancha', fr: 'Repassage', ru: 'Глажка', de: 'Bügeln', az: 'Ütü', it: 'Stiratura', ar: 'كي' },
    'terzi tadilat': { tr: 'Terzi Tadilat', en: 'Tailoring Repair', es: 'Reparación de Sastrería', fr: 'Retouche', ru: 'Ремонт одежды', de: 'Schneidereireparatur', az: 'Dərzi Təmiri', it: 'Riparazione Sartoriale', ar: 'إصلاح خياطة' },
    terzi: { tr: 'Terzi', en: 'Tailoring', es: 'Sastrería', fr: 'Couture', ru: 'Ателье', de: 'Schneiderei', az: 'Dərzi', it: 'Sartoria', ar: 'خياطة' },
    lostra: { tr: 'Lostra', en: 'Shoe Shine', es: 'Lustrado de Zapatos', fr: 'Cirage', ru: 'Чистка обуви', de: 'Schuhputzen', az: 'Ayaqqabı Cilalanması', it: 'Lucidatura Scarpe', ar: 'تلميع الأحذية' },
    
    // Colors
    beyaz: { tr: 'Beyaz', en: 'White', es: 'Blanco', ru: 'Белый', de: 'Weiß', az: 'Ağ', it: 'Bianco', ar: 'أبيض' },
    siyah: { tr: 'Siyah', en: 'Black', es: 'Negro', ru: 'Черный', de: 'Schwarz', az: 'Qara', it: 'Nero', ar: 'أسود' },
    kırmızı: { tr: 'Kırmızı', en: 'Red', es: 'Rojo', ru: 'Красный', de: 'Rot', az: 'Qırmızı', it: 'Rosso', ar: 'أحمر' },
    mavi: { tr: 'Mavi', en: 'Blue', es: 'Azul', ru: 'Синий', de: 'Blau', az: 'Mavi', it: 'Blu', ar: 'أزرق' },
    yeşil: { tr: 'Yeşil', en: 'Green', es: 'Verde', ru: 'Зеленый', de: 'Grün', az: 'Yaşıl', it: 'Verde', ar: 'أخضر' },
    sarı: { tr: 'Sarı', en: 'Yellow', es: 'Amarillo', ru: 'Желтый', de: 'Gelb', az: 'Sarı', it: 'Giallo', ar: 'أصفر' },
    turuncu: { tr: 'Turuncu', en: 'Orange', es: 'Naranja', ru: 'Оранжевый', de: 'Orange', az: 'Narıncı', it: 'Arancione', ar: 'برتقالي' },
    mor: { tr: 'Mor', en: 'Purple', es: 'Morado', ru: 'Фиолетовый', de: 'Lila', az: 'Bənövşəyi', it: 'Viola', ar: 'بنفسجي' },
    pembe: { tr: 'Pembe', en: 'Pink', es: 'Rosa', ru: 'Розовый', de: 'Rosa', az: 'Çəhrayı', it: 'Rosa', ar: 'وردي' },
    kahverengi: { tr: 'Kahverengi', en: 'Brown', es: 'Marrón', ru: 'Коричневый', de: 'Braun', az: 'Qəhvəyi', it: 'Marrone', ar: 'بني' },
    gri: { tr: 'Gri', en: 'Gray', es: 'Gris', ru: 'Серый', de: 'Grau', az: 'Boz', it: 'Grigio', ar: 'رمادي' },
    lacivert: { tr: 'Lacivert', en: 'Navy Blue', es: 'Azul Marino', ru: 'Темно-синий', de: 'Marineblau', az: 'Tünd göy', it: 'Blu Scuro', ar: 'كحلي' },
    bej: { tr: 'Bej', en: 'Beige', es: 'Beige', ru: 'Бежевый', de: 'Beige', az: 'Bej', it: 'Beige', ar: 'بيج' },
    krem: { tr: 'Krem', en: 'Cream', es: 'Crema', ru: 'Кремовый', de: 'Creme', az: 'Krem', it: 'Crema', ar: 'كريمي' },
    
    // Stain Types
    yok: { tr: 'Yok', en: 'None', es: 'Ninguno', ru: 'Нет', de: 'Keine', az: 'Yoxdur', it: 'Nessuna', ar: 'لا يوجد' },
    'yağ lekesi': { tr: 'Yağ Lekesi', en: 'Oil Stain', es: 'Mancha de Aceite', ru: 'Масляное пятно', de: 'Ölfleck', az: 'Yağ ləkəsi', it: "Macchia d'Olio", ar: 'بقعة زيت' },
    'mürekkep lekesi': { tr: 'Mürekkep Lekesi', en: 'Ink Stain', es: 'Mancha de Tinta', ru: 'Пятно от чернил', de: 'Tintenfleck', az: 'Mürəkkəb ləkəsi', it: "Macchia d'Inchiostro", ar: 'بقعة حبر' },
    'kan lekesi': { tr: 'Kan Lekesi', en: 'Blood Stain', es: 'Mancha de Sangre', ru: 'Пятно крови', de: 'Blutfleck', az: 'Qan ləkəsi', it: 'Macchia di Sangue', ar: 'بقعة دم' },
    'çikolata lekesi': { tr: 'Çikolata Lekesi', en: 'Chocolate Stain', es: 'Mancha de Chocolate', ru: 'Пятно от шоколада', de: 'Schokoladenfleck', az: 'Şokolad ləkəsi', it: 'Macchia di Cioccolato', ar: 'بقعة شوكولاتة' },
    'kahve lekesi': { tr: 'Kahve Lekesi', en: 'Coffee Stain', es: 'Mancha de Café', ru: 'Пятно от кофе', de: 'Kaffeefleck', az: 'Qəhvə ləkəsi', it: 'Macchia di Caffè', ar: 'بقعة قهوة' },
    'şarap lekesi': { tr: 'Şarap Lekesi', en: 'Wine Stain', es: 'Mancha de Vino', ru: 'Пятно от вина', de: 'Weinfleck', az: 'Şərab ləkəsi', it: 'Macchia di Vino', ar: 'بقعة نبيذ' },
    'çamur lekesi': { tr: 'Çamur Lekesi', en: 'Mud Stain', es: 'Mancha de Lodo', ru: 'Грязевое пятно', de: 'Schlammfleck', az: 'Palçıq ləkəsi', it: 'Macchia di Fango', ar: 'بقعة طين' },
    'boya lekesi': { tr: 'Boya Lekesi', en: 'Paint Stain', es: 'Mancha de Pintura', ru: 'Пятно от краски', de: 'Farbfleck', az: 'Boya ləkəsi', it: 'Macchia di Vernice', ar: 'بقعة طلاء' },
    kan: { tr: 'Kan', en: 'Blood', es: 'Sangre', ru: 'Кровь', de: 'Blut', az: 'Qan', it: 'Sangue', ar: 'دم' },
    yağ: { tr: 'Yağ', en: 'Oil', es: 'Aceite', ru: 'Масло', de: 'Öl', az: 'Yağ', it: 'Olio', ar: 'زيت' },
    mürekkep: { tr: 'Mürekkep', en: 'Ink', es: 'Tinta', ru: 'Чернила', de: 'Tinte', az: 'Mürəkkəb', it: 'Inchiostro', ar: 'حبر' },
    'çay/kahve': { tr: 'Çay/Kahve', en: 'Tea/Coffee', es: 'Té/Café', ru: 'Чай/Кофе', de: 'Tee/Kaffee', az: 'Çay/Qəhvə', it: 'Tè/Caffè', ar: 'شاي/قهوة' },
    meyve: { tr: 'Meyve', en: 'Fruit', es: 'Fruta', ru: 'Фрукты', de: 'Obst', az: 'Meyvə', it: 'Frutta', ar: 'فواكه' },
    pas: { tr: 'Pas', en: 'Rust', es: 'Óxido', ru: 'Ржавчина', de: 'Rost', az: 'Pas', it: 'Ruggine', ar: 'صدأ' },
    boya: { tr: 'Boya', en: 'Paint', es: 'Pintura', ru: 'Краска', de: 'Farbe', az: 'Boya', it: 'Vernice', ar: 'طلاء' },
    çamur: { tr: 'Çamur', en: 'Mud', es: 'Lodo', ru: 'Грязь', de: 'Schlamm', az: 'Palçıq', it: 'Fango', ar: 'طين' },
    tuz: { tr: 'Tuz', en: 'Salt', es: 'Sal', ru: 'Соль', de: 'Salz', az: 'Duz', it: 'Sale', ar: 'ملح' },
    
    // Damage Types
    sökük: { tr: 'Sökük', en: 'Tear', es: 'Desgarro', ru: 'Разрыв', de: 'Riss', az: 'Cırıq', it: 'Strappo', ar: 'تمزق' },
    delik: { tr: 'Delik', en: 'Hole', es: 'Agujero', ru: 'Дырка', de: 'Loch', az: 'Deşik', it: 'Buco', ar: 'ثقب' },
    yanık: { tr: 'Yanık', en: 'Burn', es: 'Quemadura', ru: 'Ожог', de: 'Brandfleck', az: 'Yanıq', it: 'Bruciatura', ar: 'حرق' },
    yırtık: { tr: 'Yırtık', en: 'Rip', es: 'Rasgadura', ru: 'Порыв', de: 'Riss', az: 'Cırılmış', it: 'Strappo', ar: 'تمزق' },
    'düğme eksik': { tr: 'Düğme Eksik', en: 'Missing Button', es: 'Botón Faltante', ru: 'Отсутствует пуговица', de: 'Fehlender Knopf', az: 'Düymə çatışmır', it: 'Bottone Mancante', ar: 'زر مفقود' },
    'fermuar bozuk': { tr: 'Fermuar Bozuk', en: 'Broken Zipper', es: 'Cremallera Rota', ru: 'Сломанная молния', de: 'Kaputter Reißverschluss', az: 'Fermuar sıradan çıxıb', it: 'Cerniera Rotta', ar: 'سحاب مكسور' },
    leke: { tr: 'Leke', en: 'Stain', es: 'Mancha', ru: 'Пятно', de: 'Fleck', az: 'Ləkə', it: 'Macchia', ar: 'بقعة' },
    soluk: { tr: 'Soluk', en: 'Faded', es: 'Descolorido', ru: 'Выцветший', de: 'Verblasst', az: 'Solğun', it: 'Sbiadito', ar: 'باهت' },
    'renk atması': { tr: 'Renk Atması', en: 'Color Bleeding', es: 'Decoloración', ru: 'Потеря цвета', de: 'Farbverlust', az: 'Rəng atmış', it: 'Perdita di Colore', ar: 'نزيف اللون' },
    'kopuk düğme': { tr: 'Kopuk Düğme', en: 'Loose Button', es: 'Botón Suelto', ru: 'Свободная пуговица', de: 'Loser Knopf', az: 'Boş düymə', it: 'Bottone Allentato', ar: 'زر مفكوك' },
    'eksik düğme': { tr: 'Eksik Düğme', en: 'Missing Button', es: 'Botón Faltante', ru: 'Отсутствует пуговица', de: 'Fehlender Knopf', az: 'Düymə çatışmır', it: 'Bottone Mancante', ar: 'زر مفقود' },
    'kopuk dikiş': { tr: 'Kopuk Dikiş', en: 'Loose Stitch', es: 'Costura Suelta', ru: 'Свободный шов', de: 'Lose Naht', az: 'Boş tikiş', it: 'Punto Allentato', ar: 'خياطة مفككة' },
    solmuş: { tr: 'Solmuş', en: 'Faded', es: 'Descolorido', ru: 'Выцветший', de: 'Verblasst', az: 'Solğun', it: 'Sbiadito', ar: 'باهت' },
    çizik: { tr: 'Çizik', en: 'Scratch', es: 'Rasguño', ru: 'Царапина', de: 'Kratzer', az: 'Cızıq', it: 'Graffio', ar: 'خدش' },
    
    // Tailoring Operations
    paca_boyu: { tr: 'Paça Boyu', en: 'Hem Length', fr: 'Longueur ourlet', es: 'Largo del Dobladillo', ru: 'Длина подола', de: 'Saumlänge', az: 'Ətək uzunluğu', it: 'Lunghezza Orlo', ar: 'طول الحاشية', pt: 'Comprimento da bainha', zh: '裤脚长度', ja: '裾丈' },
    bel_ayari: { tr: 'Bel Ayarı', en: 'Waist Adjustment', fr: 'Ajustement taille', es: 'Ajuste de Cintura', ru: 'Корректировка талии', de: 'Taillenanpassung', az: 'Bel tənzimlənməsi', it: 'Regolazione Vita', ar: 'تعديل الخصر', pt: 'Ajuste de cintura', zh: '腰部调整', ja: 'ウエスト調整' },
    kol_boyu: { tr: 'Kol Boyu', en: 'Sleeve Length', fr: 'Longueur manche', es: 'Largo de Manga', ru: 'Длина рукава', de: 'Ärmellänge', az: 'Qol uzunluğu', it: 'Lunghezza Manica', ar: 'طول الكم', pt: 'Comprimento da manga', zh: '袖长', ja: '袖丈' },
    fermuar_degisimi: { tr: 'Fermuar Değişimi', en: 'Zipper Replacement', fr: 'Remplacement fermeture', es: 'Cambio de Cremallera', ru: 'Замена молнии', de: 'Reißverschlussaustausch', az: 'Fermuar dəyişdirilməsi', it: 'Sostituzione Cerniera', ar: 'استبدال السحاب', pt: 'Substituição de zíper', zh: '拉链更换', ja: 'ファスナー交換' },
    dugme_dikme: { tr: 'Düğme Dikme', en: 'Button Sewing', fr: 'Coudre bouton', es: 'Coser Botón', ru: 'Пришивание пуговицы', de: 'Knopfannähen', az: 'Düymə tikilməsi', it: 'Cucitura Bottone', ar: 'خياطة الزر', pt: 'Costura de botão', zh: '缝纽扣', ja: 'ボタン付け' },
    sokuk_dikimi: { tr: 'Sökük Dikimi', en: 'Tear Repair', fr: 'Réparation déchirure', es: 'Reparación de Desgarro', ru: 'Ремонт разрыва', de: 'Rissreparatur', az: 'Cırıq tikilməsi', it: 'Riparazione Strappo', ar: 'إصلاح تمزق', pt: 'Reparo de rasgo', zh: '修补裂缝', ja: '破れ修理' },
    yama: { tr: 'Yama', en: 'Patch', fr: 'Pièce', es: 'Parche', ru: 'Заплатка', de: 'Flicken', az: 'Yamaq', it: 'Toppa', ar: 'رقعة', pt: 'Remendo', zh: '打补丁', ja: 'あて布' },
    astar_degisimi: { tr: 'Astar Değişimi', en: 'Lining Replacement', fr: 'Remplacement doublure', es: 'Cambio de Forro', ru: 'Замена подкладки', de: 'Futteraustausch', az: 'Astar dəyişdirilməsi', it: 'Sostituzione Fodera', ar: 'استبدال البطانة', pt: 'Substituição de forro', zh: '更换内衬', ja: '裏地交換' },
    omuz_ayari: { tr: 'Omuz Ayarı', en: 'Shoulder Adjustment', fr: 'Ajustement épaule', es: 'Ajuste de Hombros', ru: 'Корректировка плеч', de: 'Schulteranpassung', az: 'Çiyin tənzimlənməsi', it: 'Regolazione Spalle', ar: 'تعديل الكتف', pt: 'Ajuste de ombro', zh: '肩部调整', ja: '肩調整' },
    cep_dikimi: { tr: 'Cep Dikimi', en: 'Pocket Sewing', fr: 'Couture poche', es: 'Coser Bolsillo', ru: 'Пришивание кармана', de: 'Taschenannähen', az: 'Cib tikilməsi', it: 'Cucitura Tasca', ar: 'خياطة الجيب', pt: 'Costura de bolso', zh: '口袋缝制', ja: 'ポケット縫い' },
  };

  return dbTranslations[value]?.[language] || dbTranslations[value.toLowerCase()]?.[language] || value;
}

// Helper function to get product name in the correct language
// Always returns uppercase for consistency
export function getProductName(product: any, language: string): string {
  let name = '';
  
  // New JSONB-based multi-language support
  if (product.names && typeof product.names === 'object') {
    // Try current language
    if (product.names[language]) {
      name = product.names[language];
    }
    // Fallback: current lang -> en -> first available
    else if (product.names['en']) {
      name = product.names['en'];
    } else {
      // Return first available language
      const firstAvailable = Object.values(product.names).find(n => n);
      name = firstAvailable as string || '';
    }
  }
  
  // Legacy support for old column-based structure (backward compatibility)
  // Only override JSONB result when a specific legacy column exists for this language
  if (language === 'it' && product.nameIt) {
    name = product.nameIt;
  } else if (language === 'az' && product.nameAz) {
    name = product.nameAz;
  } else if (language === 'de' && product.nameDe) {
    name = product.nameDe;
  } else if (language === 'ru' && product.nameRu) {
    name = product.nameRu;
  } else if (language === 'es' && product.nameEs) {
    name = product.nameEs;
  } else if (language === 'en' && product.nameEn) {
    name = product.nameEn;
  } else if (!name) {
    // Only fall back to Turkish name if JSONB block found nothing
    name = product.name || '';
  }
  
  // Always return uppercase for consistency
  return name.toUpperCase();
}